import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Sparkles, Lock, Clock, Calendar, FileText, AlertCircle,
} from "lucide-react";
import { useLang } from "@/lib/lang-context";
import { useAuth } from "@/lib/auth-context";
import { getBillingMe, startCreditCheckout, startPremiumCheckout } from "@/lib/api";
import { trackEvent } from "@/lib/analytics";
import { toast } from "sonner";

/**
 * "Il mio piano" — personal billing dashboard for the logged-in user.
 *
 * Three personas it serves:
 *   1. Free user (default after sign-up) — needs to see how many free
 *      reports remain and a clear upgrade path.
 *   2. Credit-holder — bought one-shot credits, sees the balance and can
 *      top up or upgrade.
 *   3. Premium — sees the expiry date + days remaining + a "Renew" CTA
 *      that re-opens Stripe Checkout (we model premium as 30-day one-time
 *      charges, so renewal is just another purchase).
 *
 * Anonymous visitors get the sign-in gate, identical pattern to /history,
 * placed AFTER all hooks so the rules-of-hooks order stays consistent.
 *
 * Data sources
 * ------------
 * * `GET /api/billing/me` returns `{tier, reports_used, report_credits,
 *   premium_until}` — the single source of truth for this page.
 * * `POST /api/billing/checkout/{credit|premium}` from `/lib/api.js`
 *   redirects to Stripe Checkout (existing wiring).
 */
const FREE_REPORT_LIMIT = 3;

const fmtDate = (iso, lang) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return new Intl.DateTimeFormat(lang === "it" ? "it-IT" : "en-GB", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    }).format(d);
  } catch {
    return iso;
  }
};

const daysUntil = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  const ms = d.getTime() - Date.now();
  return Math.max(0, Math.ceil(ms / (1000 * 60 * 60 * 24)));
};

const Plan = () => {
  const { t, lang } = useLang();
  const { user, loading: authLoading } = useAuth();
  const [billing, setBilling] = useState(null);
  const [loading, setLoading] = useState(true);
  const [buying, setBuying] = useState(null); // "credit" | "premium" | null

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await getBillingMe();
        if (!cancelled) setBilling(data);
      } catch {
        if (!cancelled) setBilling(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user]);

  const handlePurchase = async (kind) => {
    setBuying(kind);
    try {
      const fn = kind === "credit" ? startCreditCheckout : startPremiumCheckout;
      const { url } = await fn(window.location.origin);
      if (!url) throw new Error("no_url");
      trackEvent("checkout_started", {
        kind,
        value: kind === "credit" ? 1.99 : 9.9,
        currency: "EUR",
        source: "plan_page",
      });
      window.location.href = url;
    } catch (err) {
      setBuying(null);
      toast.error(err?.response?.data?.detail || t("paywall.failed"));
    }
  };

  // ---------- Anonymous gate (after hooks for rules-of-hooks compliance) ----------
  if (!authLoading && !user) {
    return (
      <main
        data-testid="plan-gate"
        className="max-w-2xl mx-auto px-6 md:px-10 py-20 text-center"
      >
        <div className="inline-flex items-center justify-center bg-[#F2EFE6] border border-[#E6E4DD] p-3 mb-6">
          <Lock size={20} strokeWidth={1.5} className="text-[#4A5D23]" />
        </div>
        <div className="label-eyebrow">{t("plan.eyebrow")}</div>
        <h1 className="font-serif-display text-4xl md:text-5xl mt-3 text-[#282C2A]">
          {t("plan.title")}
        </h1>
        <p className="mt-4 text-[#6C706D]">{t("plan.gateMessage")}</p>
        <Link
          to="/login"
          data-testid="plan-gate-cta"
          className="inline-flex items-center gap-2 mt-8 px-5 py-2.5 text-xs font-semibold uppercase tracking-wider bg-[#282C2A] text-white hover:bg-[#4A5D23] transition-colors"
        >
          {t("quota.registerCta")}
        </Link>
      </main>
    );
  }

  if (loading) {
    return (
      <main className="max-w-4xl mx-auto px-6 md:px-10 py-20 text-center text-[#6C706D] font-mono text-xs uppercase tracking-widest">
        {t("plan.loading")}
      </main>
    );
  }

  const tier = billing?.tier || "free";
  const reportsUsed = billing?.reports_used ?? 0;
  const credits = billing?.report_credits ?? 0;
  const premiumUntil = billing?.premium_until;
  const isPremium = tier === "premium" && premiumUntil;
  const remainingFree = Math.max(0, FREE_REPORT_LIMIT - reportsUsed);
  const days = isPremium ? daysUntil(premiumUntil) : null;

  // ----- Header card content varies per tier -----
  const HeaderCard = () => {
    if (isPremium) {
      return (
        <div
          data-testid="plan-status-card"
          data-tier="premium"
          className="border-2 border-[#4A5D23] bg-[#FAFBF6] p-6 md:p-8"
        >
          <div className="flex items-start gap-4 flex-wrap">
            <div className="bg-[#4A5D23] text-white p-3">
              <Sparkles size={20} strokeWidth={1.5} />
            </div>
            <div className="flex-1 min-w-[240px]">
              <div className="label-eyebrow text-[#4A5D23]">{t("plan.premiumActiveEyebrow")}</div>
              <h2 className="font-serif-display text-3xl md:text-4xl mt-1 text-[#282C2A]">
                {t("plan.premiumActiveTitle")}
              </h2>
              <p className="mt-2 text-sm text-[#6C706D]">
                {t("plan.premiumActiveSub")}
              </p>
            </div>
            <div className="text-right">
              <div className="label-eyebrow">{t("plan.expires")}</div>
              <div
                data-testid="plan-premium-expiry"
                className="font-mono text-base mt-1 text-[#282C2A]"
              >
                {fmtDate(premiumUntil, lang)}
              </div>
              {days != null && (
                <div className="font-mono text-xs mt-1 text-[#4A5D23]">
                  {days === 0
                    ? t("plan.expiresToday")
                    : `${days} ${t("plan.daysLeft")}`}
                </div>
              )}
            </div>
          </div>
        </div>
      );
    }
    return (
      <div
        data-testid="plan-status-card"
        data-tier={tier}
        className="border-2 border-[#282C2A] bg-white p-6 md:p-8"
      >
        <div className="flex items-start gap-4 flex-wrap">
          <div className="bg-[#282C2A] text-white p-3">
            <FileText size={20} strokeWidth={1.5} />
          </div>
          <div className="flex-1 min-w-[240px]">
            <div className="label-eyebrow">{t("plan.freeEyebrow")}</div>
            <h2 className="font-serif-display text-3xl md:text-4xl mt-1 text-[#282C2A]">
              {t("plan.freeTitle")}
            </h2>
            <p className="mt-2 text-sm text-[#6C706D]">{t("plan.freeSub")}</p>
          </div>
          <div className="text-right">
            <div className="label-eyebrow">{t("plan.reportsRemaining")}</div>
            <div
              data-testid="plan-remaining-free"
              className="font-serif-display text-3xl mt-1 text-[#282C2A]"
            >
              {remainingFree}
              <span className="font-mono text-sm text-[#6C706D]">
                {" "}
                / {FREE_REPORT_LIMIT}
              </span>
            </div>
            {credits > 0 && (
              <div
                data-testid="plan-credit-balance"
                className="font-mono text-xs mt-1 text-[#4A5D23]"
              >
                + {credits} {t("plan.creditsAvailable")}
              </div>
            )}
          </div>
        </div>
        {remainingFree === 0 && credits === 0 && (
          <div
            data-testid="plan-exhausted-warn"
            className="mt-5 border border-[#E0BBA0] bg-[#FFF1E6] px-3 py-2 text-xs text-[#7A3A12] flex items-center gap-2"
          >
            <AlertCircle size={13} strokeWidth={1.75} />
            <span>{t("plan.exhaustedWarn")}</span>
          </div>
        )}
      </div>
    );
  };

  return (
    <main data-testid="plan-page" className="max-w-5xl mx-auto px-6 md:px-10 py-16">
      <div className="label-eyebrow flex items-center gap-2">
        <span className="inline-block h-1.5 w-1.5 bg-[#4A5D23]" />
        {t("plan.eyebrow")}
      </div>
      <h1 className="font-serif-display text-4xl md:text-5xl mt-3 text-[#282C2A]">
        {t("plan.title")}
      </h1>
      <p className="mt-2 text-sm text-[#6C706D]">{t("plan.sub")}</p>

      <div className="mt-10">
        <HeaderCard />
      </div>

      {/* Upgrade / renew options */}
      <div className="mt-10">
        <div className="label-eyebrow mb-4">
          {isPremium ? t("plan.renewSection") : t("plan.upgradeSection")}
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <div
            data-testid="plan-credit-card"
            className="border border-[#E6E4DD] bg-white p-5 flex flex-col"
          >
            <div className="flex items-center gap-2 text-[#282C2A]">
              <FileText size={14} strokeWidth={1.75} />
              <span className="label-eyebrow">{t("paywall.creditEyebrow")}</span>
            </div>
            <h3 className="font-serif-display text-2xl mt-2 text-[#282C2A]">
              {t("paywall.creditTitle")}
            </h3>
            <p className="mt-1 text-xs text-[#6C706D]">{t("paywall.creditSub")}</p>
            <div className="mt-4 font-serif-display text-3xl text-[#282C2A]">
              €1.99
              <span className="font-mono text-[10px] uppercase tracking-widest text-[#6C706D] ml-2">
                {t("paywall.creditUnit")}
              </span>
            </div>
            <button
              type="button"
              onClick={() => handlePurchase("credit")}
              disabled={buying != null}
              data-testid="plan-buy-credit"
              className="mt-auto pt-5 inline-flex items-center justify-center gap-2 px-4 py-2.5 text-xs font-semibold uppercase tracking-wider border border-[#282C2A] text-[#282C2A] hover:bg-[#282C2A] hover:text-white transition-colors disabled:opacity-50"
            >
              {buying === "credit" ? t("paywall.loading") : t("paywall.creditCta")}
            </button>
          </div>

          <div
            data-testid="plan-premium-card"
            className="border-2 border-[#4A5D23] bg-white p-5 flex flex-col relative"
          >
            <div className="absolute top-0 right-4 -translate-y-1/2 bg-[#4A5D23] text-white text-[10px] font-mono uppercase tracking-widest px-2 py-1">
              {isPremium ? t("plan.renewBadge") : t("paywall.premiumBadge")}
            </div>
            <div className="flex items-center gap-2 text-[#4A5D23]">
              <Sparkles size={14} strokeWidth={1.75} />
              <span className="label-eyebrow text-[#4A5D23]">
                {t("paywall.premiumEyebrow")}
              </span>
            </div>
            <h3 className="font-serif-display text-2xl mt-2 text-[#282C2A]">
              {t("paywall.premiumTitle")}
            </h3>
            <p className="mt-1 text-xs text-[#6C706D]">{t("paywall.premiumSub")}</p>
            <div className="mt-4 font-serif-display text-3xl text-[#282C2A]">
              €9.90
              <span className="font-mono text-[10px] uppercase tracking-widest text-[#6C706D] ml-2">
                {t("paywall.premiumUnit")}
              </span>
            </div>
            <button
              type="button"
              onClick={() => handlePurchase("premium")}
              disabled={buying != null}
              data-testid="plan-buy-premium"
              className="mt-auto pt-5 inline-flex items-center justify-center gap-2 px-4 py-2.5 text-xs font-semibold uppercase tracking-wider bg-[#4A5D23] text-white hover:bg-[#3B4A1C] transition-colors disabled:opacity-50"
            >
              {buying === "premium"
                ? t("paywall.loading")
                : isPremium
                ? t("plan.renewCta")
                : t("paywall.premiumCta")}
            </button>
          </div>
        </div>
        <p className="mt-4 text-[11px] text-[#6C706D]">{t("paywall.footer")}</p>
      </div>

      {/* Quick stats footer */}
      <div className="mt-12 grid grid-cols-3 gap-4 text-center">
        <div className="border-l border-[#E6E4DD] pl-4">
          <div className="label-eyebrow">{t("plan.reportsUsed")}</div>
          <div className="font-mono text-base mt-1 text-[#282C2A]">{reportsUsed}</div>
        </div>
        <div className="border-l border-[#E6E4DD] pl-4">
          <div className="label-eyebrow">{t("plan.creditsBalance")}</div>
          <div className="font-mono text-base mt-1 text-[#282C2A]">{credits}</div>
        </div>
        <div className="border-l border-[#E6E4DD] pl-4">
          <div className="label-eyebrow flex items-center justify-center gap-1">
            {isPremium ? <Clock size={11} strokeWidth={1.75} /> : <Calendar size={11} strokeWidth={1.75} />}
            {isPremium ? t("plan.daysLeftShort") : t("plan.freeRemainingShort")}
          </div>
          <div className="font-mono text-base mt-1 text-[#282C2A]">
            {isPremium ? `${days ?? "—"}` : `${remainingFree} / ${FREE_REPORT_LIMIT}`}
          </div>
        </div>
      </div>
    </main>
  );
};

export default Plan;
